import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useAuth } from "./useAuth";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import type { CourseWithTrail } from "@/types";

export const useRecommendation = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [loadingCourseId, setLoadingCourseId] = useState<string | null>(null);
  const { supabase } = useSupabaseConnection();

  const toggleRecommendation = useMutation({
    mutationFn: async ({ courseId, isCurrentlyRecommended }: { 
      courseId: string; 
      isCurrentlyRecommended: boolean; 
    }) => {
      if (!user) {
        throw new Error("로그인이 필요합니다.");
      }

      if (isCurrentlyRecommended) {
        // 추천 취소: course_recommendations에서 삭제하고 recommend_count 감소
        const { error: deleteError } = await supabase
          .from("course_recommendations")
          .delete()
          .eq("user_id", user.id)
          .eq("course_id", courseId);

        if (deleteError) {
          throw new Error(`추천 취소 실패: ${deleteError.message}`);
        }

        // recommend_count 감소
        const { error: updateError } = await supabase.rpc('decrement_recommend_count', {
          course_id_param: courseId
        });

        if (updateError) {
          throw new Error(`추천 수 업데이트 실패: ${updateError.message}`);
        }

        return { action: 'removed', courseId };
      } else {
        // 추천 추가: course_recommendations에 추가하고 recommend_count 증가
        const { error: insertError } = await supabase
          .from("course_recommendations")
          .insert({
            user_id: user.id,
            course_id: courseId
          });

        if (insertError) {
          throw new Error(`추천 추가 실패: ${insertError.message}`);
        }

        // recommend_count 증가
        const { error: updateError } = await supabase.rpc('increment_recommend_count', {
          course_id_param: courseId
        });

        if (updateError) {
          throw new Error(`추천 수 업데이트 실패: ${updateError.message}`);
        }

        return { action: 'added', courseId };
      }
    },
    onMutate: async ({ courseId, isCurrentlyRecommended }) => {
      // 개별 카드 로딩 상태 시작
      setLoadingCourseId(courseId);
      
      // useCourses와 동일한 쿼리 키 사용하여 일관성 확보
      const queryKey = ["courses", user?.id || "anonymous"];
      console.log('🔄 낙관적 업데이트 시작:', { 
        queryKey, 
        courseId, 
        isCurrentlyRecommended,
        userId: user?.id 
      });
      
      // 낙관적 업데이트를 위해 이전 데이터 백업
      await queryClient.cancelQueries({ queryKey });
      
      const previousCourses = queryClient.getQueryData<CourseWithTrail[]>(queryKey);
      console.log('📊 이전 데이터:', previousCourses ? `${previousCourses.length}개 코스` : '데이터 없음');
      
      // 낙관적 업데이트: UI를 즉시 업데이트
      queryClient.setQueryData<CourseWithTrail[]>(queryKey, (old) => {
        if (!old || !Array.isArray(old)) {
          console.warn('⚠️ 낙관적 업데이트 실패: 유효하지 않은 데이터', old);
          return old;
        }
        
        const updatedData = old.map((course) => {
          if (course.course_id === courseId) {
            const newRecommendCount = isCurrentlyRecommended 
              ? Math.max((course.recommend_count || 0) - 1, 0) 
              : (course.recommend_count || 0) + 1;
              
            const updatedCourse = {
              ...course,
              is_recommended: !isCurrentlyRecommended,
              recommend_count: newRecommendCount
            };
            
            console.log('✨ 코스 상태 업데이트:', {
              courseId,
              이전_추천상태: isCurrentlyRecommended,
              새_추천상태: !isCurrentlyRecommended,
              이전_추천수: course.recommend_count,
              새_추천수: newRecommendCount
            });
            
            return updatedCourse;
          }
          return course;
        });
        
        return updatedData;
      });

      console.log('💾 낙관적 업데이트 완료');
      return { previousCourses, queryKey };
    },
    onError: (error, variables, context) => {
      // 개별 카드 로딩 상태 종료
      setLoadingCourseId(null);
      
      // 에러 발생 시 이전 상태로 롤백
      if (context?.previousCourses && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousCourses);
      }
      
      console.error("추천 토글 에러:", error);
      toast({
        title: "추천 처리 실패",
        description: error.message || "추천 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
        variant: "destructive"
      });
    },
    onSuccess: (data) => {
      // 개별 카드 로딩 상태 종료
      setLoadingCourseId(null);
      
      // 성공 시 서버 데이터와 동기화 - useCourses와 동일한 쿼리 키 사용
      const queryKey = ["courses", user?.id || "anonymous"];
      console.log('✅ 추천 처리 성공:', { action: data.action, courseId: data.courseId, queryKey });
      
      queryClient.invalidateQueries({ queryKey });
      
      const message = data.action === 'added' 
        ? "코스를 추천했습니다! ❤️" 
        : "추천을 취소했습니다.";
      const title = data.action === 'added' 
        ? "추천 완료" 
        : "추천 취소";
        
      toast({
        title,
        description: message,
      });
    },
  });

  const handleRecommendation = (courseId: string, isCurrentlyRecommended: boolean) => {
    // Auth가 아직 로딩 중이면 잠시 대기
    if (loading) {
      setTimeout(() => {
        toast({
          title: "잠시만 기다려주세요",
          description: "로그인 상태를 확인하고 있습니다.",
          variant: "default",
        });
      }, 0);
      return;
    }
    
    if (!isAuthenticated) {
      // 작은 지연을 추가하여 UI가 준비될 시간을 줌
      setTimeout(() => {
        toast({
          title: "로그인 필요",
          description: "로그인 후 추천하실 수 있습니다.",
          variant: "destructive",
        });
      }, 0);
      return;
    }

    toggleRecommendation.mutate({ courseId, isCurrentlyRecommended });
  };

  return {
    handleRecommendation,
    isLoading: toggleRecommendation.isPending,
    isLoadingCourse: (courseId: string) => loadingCourseId === courseId,
  };
};